import URI from 'urijs';
import Vue from 'vue';
import { status, orderBy, currencySymbols } from '@/config/settlements';
import { sponsors } from '@/config/authorization';
import { config } from '@/plugins/env.config';
import { decamelizeKeys } from 'humps';

async function getSettlements (filters, attemptNumber = 1){
    const getSettlementsUri = new URI(config.apiBaseUrl)
    getSettlementsUri.segment('settlements')
    getSettlementsUri.segment('v3')
    getSettlementsUri.segment('settlements')

    if(filters.createdFrom){
        getSettlementsUri.addSearch('date_from', filters.createdFrom)
    }

    if(filters.createdTo){
        getSettlementsUri.addSearch('date_to', filters.createdTo)
    }

    if(filters.sponsorSlug){
        getSettlementsUri.addSearch('sponsor_slug', filters.sponsorSlug)
    }

    if(filters.codeSearchValue){
        getSettlementsUri.addSearch('sponsor_settlement_id', filters.codeSearchValue)
    }

    getSettlementsUri.addSearch('included_statuses', filters.includedStatuses.join(','))
    getSettlementsUri.addSearch('page', filters.page)
    getSettlementsUri.addSearch('size', filters.size)
    
    try {
        return await Vue.prototype.$http.get(getSettlementsUri.toString())
    } catch (error) {
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getSettlements(filters, attemptNumber + 1)
        }

        throw error
    }
}

async function getSettlementClaims (filters, attemptNumber = 1){
    const settlementClaimsURI = new URI(config.apiBaseUrl)
    settlementClaimsURI.segment('settlements')
    settlementClaimsURI.segment('v3')
    settlementClaimsURI.segment('settlements')
    settlementClaimsURI.segment(filters.settlementId)
    settlementClaimsURI.segment('transactions')

    if(filters.searchValue){
        settlementClaimsURI.addSearch('sponsor_transaction_id', filters.searchValue)
    }

    settlementClaimsURI.addSearch('page', filters.page)
    settlementClaimsURI.addSearch('size', filters.size)

    try{
        return await Vue.prototype.$http.get(settlementClaimsURI.toString())
    }
    catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getSettlementClaims(filters, attemptNumber + 1)
        }

        throw error
    }
}

async function getSettlementDetail (settlementId, providerSlug, attemptNumber = 1) {
    const settlementDetailURI = new URI(config.apiBaseUrl)
    settlementDetailURI.segment('settlements')
    settlementDetailURI.segment('v3')
    settlementDetailURI.segment('settlements')
    settlementDetailURI.segment(settlementId)
    settlementDetailURI.addSearch('provider_slug', providerSlug)

    try{
        return await Vue.prototype.$http.get(settlementDetailURI.toString())
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            getSettlementDetail (settlementId, providerSlug, attemptNumber + 1)
        }

        throw error
    }
}

function getSponsorSettlementId(status, sponsorSettlementId, vuei18n) {
    if(status === 'SENT_TO_SPONSOR'){
        return vuei18n.t('settlements.sponsorSettlement')
    } else {
        return sponsorSettlementId ?? 'N/A'
    }
}

function getStatusText(statusToFind) {
    let itemFound = status.find(status => {
        if(status.value === statusToFind ){
            return status.text
        }
    })

    if(itemFound){
        return itemFound.text
    }
    return ''
}

function getSponsorMedicalAccountNumber(status, sponsorMedicalAccountNumber) {
    if(status === 'REJECTED_BY_SPONSOR'){
        return 'N/A';
    }else if(status === 'IN_PROGRESS' || status === 'SENT_TO_SPONSOR'){
        return '';
    }else {
        return sponsorMedicalAccountNumber ?? '-'
    }
}

function getSponsorText(sponsorToFind) {
    let itemFound = sponsors.find(sponsor => {
        if(sponsor.value === sponsorToFind){
            return sponsor.text
        }
    })

    if(itemFound){
        return itemFound.text
    }
    return ''
}

function sanitizeAmount(currencyCode, amount, notNull){
    let localeAmount = 0;

    if(amount){
        localeAmount = amount.toLocaleString(undefined, {minimumFractionDigits: 2})
    }
    else if(notNull){
        localeAmount = "0.00"
    }
    else{
        return ''
    }

    let currencyConfig = currencySymbols.find(symbol => symbol.code === currencyCode)
    return `${currencyCode ? currencyCode : ''} ${currencyConfig ? currencyConfig.symbol : ''}${localeAmount}`
}

async function getPendingAuthorizations (filters, attemptNumber = 1){
    const pendingAuthorizationsURI = new URI(config.apiBaseUrl)
    pendingAuthorizationsURI.segment('settlements')
    pendingAuthorizationsURI.segment('v3')
    pendingAuthorizationsURI.segment('settlements')
    pendingAuthorizationsURI.segment('pending-authorizations')

    pendingAuthorizationsURI.addSearch('transaction_types', filters.transactionTypes.join(','))
    pendingAuthorizationsURI.addSearch('sponsor_slug', filters.sponsorSlug)
    pendingAuthorizationsURI.addSearch('date_from', filters.dateFrom)
    pendingAuthorizationsURI.addSearch('date_to', filters.dateTo)
    pendingAuthorizationsURI.addSearch('page', filters.page)
    pendingAuthorizationsURI.addSearch('size', filters.size)

    try{
        return await Vue.prototype.$http.get(pendingAuthorizationsURI.toString())
    }
    catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getPendingAuthorizations(filters, attemptNumber + 1)
        }

        throw error
    }
}

async function setSettlementId (sponsorSlug, attemptNumber = 1){
    const setSettlementIdURI = new URI(config.apiBaseUrl)
    setSettlementIdURI.segment('settlements')
    setSettlementIdURI.segment('v3')
    setSettlementIdURI.segment('settlements')

    try{
        return await Vue.prototype.$http.post(setSettlementIdURI.toString(), { sponsor_slug : sponsorSlug})
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return setSettlementId(sponsorSlug, attemptNumber + 1)
        }
        throw error
    }
}

async function setClaims(body, attemptNumber = 1){
    const setClaimsURI = new URI(config.apiBaseUrl)
    setClaimsURI.segment('settlements')
    setClaimsURI.segment('v3')
    setClaimsURI.segment('settlements')
    setClaimsURI.segment(body.settlementId)
    setClaimsURI.segment('claims')
    const bodyDecamelized = decamelizeKeys(body)

    try{
        return await Vue.prototype.$http.post(setClaimsURI.toString(), bodyDecamelized.params);
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return setClaims(body, attemptNumber + 1)
        }
        throw response.data
    }
}

async function deleteClaims(settlementId, claimIds, attemptNumber = 1){
    const deleteClaimsURI = new URI(config.apiBaseUrl)
    deleteClaimsURI.segment('settlements')
    deleteClaimsURI.segment('v3')
    deleteClaimsURI.segment('settlements')
    deleteClaimsURI.segment(settlementId)
    deleteClaimsURI.segment('claims')
    deleteClaimsURI.addSearch('claim_ids', claimIds.join(','))

    try{
        return await Vue.prototype.$http.delete(deleteClaimsURI.toString(), {});
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return setClaims(settlementId, claimIds, attemptNumber + 1)
        }
        throw response.data
    }
}

async function sendSettlementFiles(settlementId, filePayload, attemptNumber = 1){
    const sendSettlementFilesURI = new URI(config.apiBaseUrl)
    sendSettlementFilesURI.segment('settlements')
    sendSettlementFilesURI.segment('v3')
    sendSettlementFilesURI.segment('settlements')
    sendSettlementFilesURI.segment(settlementId)
    sendSettlementFilesURI.segment('documents')

    sendSettlementFilesURI.addQuery('document_type', filePayload.documentType)

    const formData = new FormData();
    formData.append('file', filePayload.document)

    try{
        return await Vue.prototype.$http.post(sendSettlementFilesURI, formData, {})
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return sendSettlementFiles(settlementId, filePayload, attemptNumber + 1)
        }
        throw response.data
    }
}

async function completeSettlement(settlementId, payload, attemptNumber = 1){
    const completeSettlementURI = new URI(config.apiBaseUrl)
    completeSettlementURI.segment('settlements')
    completeSettlementURI.segment('v3')
    completeSettlementURI.segment('settlements')
    completeSettlementURI.segment(settlementId)
    completeSettlementURI.segment('complete')

    payload = decamelizeKeys(payload)

    try{
        return await Vue.prototype.$http.post(completeSettlementURI.toString(), payload)
    }catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return completeSettlement(payload, settlementId, attemptNumber + 1)
        }
        throw response.data
    }
}

async function settlementSummary(settlementId, attemptNumber = 1){
    const settlementSummaryURI = new URI(config.apiBaseUrl);
    settlementSummaryURI.segment('settlements')
    settlementSummaryURI.segment('v3')
    settlementSummaryURI.segment('settlements')
    settlementSummaryURI.segment(settlementId)
    settlementSummaryURI.segment('transactions')
    settlementSummaryURI.segment('summary')

    try{
        return await Vue.prototype.$http.get(settlementSummaryURI.toString())
    }
    catch(error){
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return settlementSummary(settlementId, attemptNumber + 1)
        }

        throw error
    }
}

async function existsSettlementByClaimAndSponsor(claimId, providerSlug, attemptNumber = 1){
    const existsSettlementByClaimAndSponsorURI = new URI(config.apiBaseUrl)
    existsSettlementByClaimAndSponsorURI.segment('settlements')
    existsSettlementByClaimAndSponsorURI.segment('v3')
    existsSettlementByClaimAndSponsorURI.segment('settlements')
    existsSettlementByClaimAndSponsorURI.segment('claim')
    existsSettlementByClaimAndSponsorURI.segment(claimId)
    existsSettlementByClaimAndSponsorURI.segment('sponsor')
    existsSettlementByClaimAndSponsorURI.segment(providerSlug)

    try {
        return await Vue.prototype.$http.get(existsSettlementByClaimAndSponsorURI.toString())
    } catch (error) {
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return existsSettlementByClaimAndSponsor(claimId, providerSlug, attemptNumber + 1)
        }
        throw response.data
    }
}

async function getSettlementDocuments(payload, attemptNumber = 1){
    const getSettlementDocumentsURI = new URI(config.apiBaseUrl);
    getSettlementDocumentsURI.segment('settlements')
    getSettlementDocumentsURI.segment('v3')
    getSettlementDocumentsURI.segment('settlements')
    getSettlementDocumentsURI.segment(payload.settlementId)
    getSettlementDocumentsURI.segment('files')

    try {
        return await Vue.prototype.$http.get(getSettlementDocumentsURI.toString())
    } catch (error) {
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getSettlementDocuments(payload, attemptNumber + 1)
        }
        throw error
    }
}

async function getDocumentFile(payload, attemptNumber = 1){
    const getDocumentsFilesURI = new URI(config.apiBaseUrl);
    getDocumentsFilesURI.segment('settlements')
    getDocumentsFilesURI.segment('v3')
    getDocumentsFilesURI.segment('settlements')
    getDocumentsFilesURI.segment(payload.settlementId)
    getDocumentsFilesURI.segment('files')
    getDocumentsFilesURI.segment(payload.settlementFileId)

    try {
        return await Vue.prototype.$http.get(getDocumentsFilesURI.toString(), {responseType: 'arraybuffer'});
    } catch(error) {
        const maxAttempts = config.getTokenMaxAttempts
        const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

        const response = error.response ? error.response : error
        const status = response.status ? response.status : error.status

        if (status === 500 && attemptNumber < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
            return getDocumentFile(payload, attemptNumber + 1)
        }
        throw error
    }
}

const getSettlementWithManualAuthorization = async (settlementId, attemptNumber = 1) => {
    const getSettlementWithManualAuthorizationURI = new URI(config.apiBaseUrl)
    getSettlementWithManualAuthorizationURI.segment('settlements')
    getSettlementWithManualAuthorizationURI.segment('v3')
    getSettlementWithManualAuthorizationURI.segment('settlements')
    getSettlementWithManualAuthorizationURI.segment(settlementId)
    getSettlementWithManualAuthorizationURI.segment('manual-authorizations')
  
    try {
      return await Vue.prototype.$http.get(getSettlementWithManualAuthorizationURI.toString())
    } catch (error) {
      const maxAttempts = config.getTokenMaxAttempts
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
  
      const response = error.response ? error.response : error
      const status = response.status ? response.status : error.status
  
      if (status === 500 && attemptNumber < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
        return getSettlementWithManualAuthorization(settlementId, attemptNumber + 1)
      }
      throw error
    }
  }


export {
    status,
    orderBy,
    sponsors,
    existsSettlementByClaimAndSponsor,
    getDocumentFile,
    getSettlementDocuments,
    getSettlements,
    getStatusText,
    getSponsorText,
    getSettlementDetail,
    sanitizeAmount,
    getPendingAuthorizations,
    setSettlementId,
    setClaims,
    completeSettlement,
    sendSettlementFiles,
    settlementSummary,
    getSettlementClaims,
    deleteClaims,
    getSettlementWithManualAuthorization,
    getSponsorSettlementId,
    getSponsorMedicalAccountNumber
}